import {
  BaseResponseProps,
  generateFormRules,
  getStandardError,
} from '@qlibs/react-components';
import { Button, Form, Grid, Input, Switch, message } from 'antd';
import { CustomFormItemPer1 } from '../../user/components/CustomFormItem';
import { httpRequest } from '../../../helpers/api';
import {
  ICreateOperationModeDetail,
  OperationModeDetailProperties,
  OperationModeProperties,
  isClutchOperationModeDevice,
} from '../types/operationMode.type';
import React, { useEffect, useMemo, useState } from 'react';
import {
  OperationModeMobileConstantActions,
  OperationModeMobileConstantCancelButton,
  OperationModeMobileConstantForm,
  OperationModeMobileConstantSaveButton,
  OperationModeMobileConstantSwitchField,
  OperationModeMobileField,
} from '../mobile';

interface IProps {
  operationModeId: string;
  onClose: () => void;
}

const OperationModeDetailCreate: React.FC<IProps> = ({
  operationModeId,
  onClose,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [isLoadingAction, setIsLoadingAction] = React.useState(false);
  const [parentDeviceId, setParentDeviceId] = useState<string>('');
  const [form] = Form.useForm();

  const isClutchDevice = useMemo(
    () => isClutchOperationModeDevice(parentDeviceId),
    [parentDeviceId],
  );

  useEffect(() => {
    const fetchParent = async () => {
      try {
        const res = await httpRequest.get<
          BaseResponseProps<OperationModeProperties>
        >('/operation-mode/' + operationModeId);
        const deviceId =
          res.data.payload?.device?.uniqueId ||
          res.data.payload?.deviceId ||
          '';
        setParentDeviceId(deviceId);
      } catch (error) {
        console.error(error);
        message.error('Failed to load operation mode device');
      }
    };

    if (operationModeId) {
      fetchParent();
    }
  }, [operationModeId]);

  async function createOperationModeDetail(values: ICreateOperationModeDetail) {
    try {
      setIsLoadingAction(true);
      const formData = {
        operationModeId,
        name: values.name,
        rpmMax: isClutchDevice ? null : values.rpmMax,
        clutchMax: isClutchDevice ? values.clutchMax : null,
        consumptionRate: values.consumptionRate,
        speed:
          values.speed === '' || values.speed === undefined
            ? null
            : values.speed,
        status: values.status ? 'active' : 'inactive',
      };

      await httpRequest.post<BaseResponseProps<OperationModeDetailProperties>>(
        '/operation-mode-detail',
        formData,
      );

      message.success(`Success add operation mode detail`);
      form.resetFields();
      onClose();
    } catch (error) {
      getStandardError(error, {
        triggerFormValidation: true,
        formRef: form,
        showToast: true,
      });
    } finally {
      setIsLoadingAction(false);
    }
  }

  const handleSubmit = async (values: any) => {
    createOperationModeDetail(values);
  };

  const thresholdField = isClutchDevice ? (
    <>
      {isMobile ? (
        <OperationModeMobileField
          label='Clutch Max'
          name='clutchMax'
          rules={generateFormRules('Clutch Max', ['numeric', 'required'])}
        >
          <Input placeholder='Input clutch max' type='number' />
        </OperationModeMobileField>
      ) : (
        <CustomFormItemPer1
          label='Clutch Max'
          name='clutchMax'
          rules={generateFormRules('Clutch Max', ['numeric', 'required'])}
        >
          <Input placeholder='Input clutch max' type='number' />
        </CustomFormItemPer1>
      )}
    </>
  ) : (
    <>
      {isMobile ? (
        <OperationModeMobileField
          label='RPM Max'
          name='rpmMax'
          rules={generateFormRules('RPM Max', ['numeric', 'required'])}
        >
          <Input placeholder='Input rpm max' type='number' />
        </OperationModeMobileField>
      ) : (
        <CustomFormItemPer1
          label='RPM Max'
          name='rpmMax'
          rules={generateFormRules('RPM Max', ['numeric', 'required'])}
        >
          <Input placeholder='Input rpm max' type='number' />
        </CustomFormItemPer1>
      )}
    </>
  );

  if (isMobile) {
    return (
      <OperationModeMobileConstantForm>
        <Form
          form={form}
          name='profileForm'
          layout='vertical'
          onFinish={handleSubmit}
          autoComplete='off'
        >
          <OperationModeMobileField
            label='Name'
            name='name'
            rules={generateFormRules('Name', ['required'])}
          >
            <Input placeholder='Input name' />
          </OperationModeMobileField>
          {thresholdField}
          <OperationModeMobileField
            label='Consumption Rate'
            name='consumptionRate'
            rules={generateFormRules('Consumption Rate', ['required'])}
          >
            <Input placeholder='Input consumption rate' suffix='L/m' />
          </OperationModeMobileField>
          <OperationModeMobileField
            label='Speed Max'
            name='speed'
            rules={[
              {
                pattern: /^(?:\d+(?:\.\d+)?)?$/,
                message: 'Speed Max must be a non-negative number.',
              },
            ]}
          >
            <Input
              placeholder='Optional speed max'
              suffix='knot'
              type='number'
            />
          </OperationModeMobileField>

          <OperationModeMobileConstantSwitchField
            label='Status'
            name='status'
            valuePropName='checked'
            initialValue={true}
          >
            <Switch defaultChecked className='custom-switch' />
          </OperationModeMobileConstantSwitchField>

          <OperationModeMobileConstantActions>
            <OperationModeMobileConstantCancelButton onClick={onClose}>
              Cancel
            </OperationModeMobileConstantCancelButton>
            <OperationModeMobileConstantSaveButton
              loading={isLoadingAction}
              type='primary'
              htmlType='submit'
            >
              Save
            </OperationModeMobileConstantSaveButton>
          </OperationModeMobileConstantActions>
        </Form>
      </OperationModeMobileConstantForm>
    );
  }

  return (
    <React.Fragment>
      <Form
        form={form}
        name='profileForm'
        layout='vertical'
        onFinish={handleSubmit}
        autoComplete='off'
      >
        <CustomFormItemPer1
          label='Name'
          name='name'
          rules={generateFormRules('Name', ['required'])}
        >
          <Input placeholder='Input name' />
        </CustomFormItemPer1>
        {thresholdField}
        <CustomFormItemPer1
          label='Consumption Rate'
          name='consumptionRate'
          rules={generateFormRules('Consumption Rate', ['required'])}
        >
          <Input placeholder='Input consumption rate' suffix='L/m' />
        </CustomFormItemPer1>
        <CustomFormItemPer1
          label='Speed Max'
          name='speed'
          rules={[
            {
              pattern: /^(?:\d+(?:\.\d+)?)?$/,
              message: 'Speed Max must be a non-negative number.',
            },
          ]}
        >
          <Input placeholder='Optional speed max' suffix='knot' type='number' />
        </CustomFormItemPer1>

        <CustomFormItemPer1
          label='Status'
          name='status'
          valuePropName='checked'
          initialValue={true}
        >
          <Switch defaultChecked className='custom-switch' />
        </CustomFormItemPer1>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button loading={isLoadingAction} type='primary' htmlType='submit'>
            Save
          </Button>
        </div>
      </Form>
    </React.Fragment>
  );
};

export default OperationModeDetailCreate;
